import type { FC } from 'react';
import { useCallback } from 'react';

import { PropertyValueInput } from 'components/inputs/property/PropertyValueInput';
import { usePropertyValueEditor } from 'hooks/usePropertyValueEditor';
import type { PropertyValue } from 'state/data/dataState';
import { useUserHasEditRightsOnProperty } from 'state/selectedProject/selectedProjectHooks';
import type { EPropertyTypes, Property } from 'state/selectedTemplate/selectedTemplateStore';
import { getNodeTypeName } from 'utils/names';
import { isPropertyValueReadOnly } from 'utils/property/getPropertyEditType';
import { hasValue } from 'utils/values';

export interface NodeDetailsPropertyUpdate {
  propertyId: string;
  technicalName: string;
  propertyType: EPropertyTypes;
  newValue: PropertyValue;
}

export interface NodeDetailsPropertyProps {
  templateNodeId: string;
  property: Property;
  /** Current server value for this property, sourced from the useNodeDetail query. */
  value: PropertyValue;
  onUpdate: (update: NodeDetailsPropertyUpdate) => void;
  disabled?: boolean;
}

export const NodeDetailsProperty: FC<NodeDetailsPropertyProps> = ({
  templateNodeId,
  property,
  value,
  onUpdate,
  disabled
}) => {
  const propertyType = property.settings.propertyType;

  const isReadOnly =
    !useUserHasEditRightsOnProperty(property.id) || (disabled ?? false) || isPropertyValueReadOnly(property.settings);

  const onCommit = useCallback(
    (newValue: string) => {
      if (!hasValue(propertyType)) return;

      onUpdate({
        propertyId: property.id,
        technicalName: property.technicalName,
        propertyType,
        newValue
      });
    },
    [onUpdate, property.id, property.technicalName, propertyType]
  );

  const editor = usePropertyValueEditor({ settings: property.settings, value, onCommit });

  return (
    <PropertyValueInput
      editType={editor.editType}
      label={getNodeTypeName(property)}
      value={editor.editValue}
      onValueChange={editor.onValueChange}
      onBlur={editor.onBlur}
      onFocus={editor.onFocus}
      onKeyDown={editor.onKeyDown}
      focused={editor.focused}
      disabled={isReadOnly}
      errorMessage={editor.errorMessage}
      inputRef={editor.inputRef}
      propertyId={property.id}
      templateNodeId={templateNodeId}
    />
  );
};
